import React from "react";
import { createRoot } from "react-dom/client";
import { Info, X, Loader2, CheckCircle, XCircle } from "lucide-react";
import { toast } from "react-toastify";

let currentToastRoot = null;
let currentToastContainer = null;

const TransactionToastContent = ({ message, step, totalSteps, onClose }) => {
  const stepLabel = totalSteps > 1 ? `(${step}/${totalSteps}) ` : "";

  return (
    <>
      {/* Blocking overlay */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0, 0, 0, 0.3)",
          zIndex: 99998,
        }}
      />
      {/* Toast */}
      <div
        style={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 99999,
          width: "min(400px, calc(100vw - 32px))",
          height: 56,
          borderRadius: 16,
          border: "1px solid #B8CBE5",
          background: "#FFFFFF",
          boxShadow: "4px 4px 36px 0px #00000014",
          padding: "8px 16px 8px 8px",
          display: "flex",
          alignItems: "center",
          gap: 24,
        }}
      >
        {/* Info icon box */}
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            padding: 8,
            background: "#1570EF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Info size={20} color="#FFFFFF" strokeWidth={2} />
        </div>

        {/* Message */}
        <span
          style={{
            fontFamily: "General Sans, sans-serif",
            fontWeight: 500,
            fontSize: 14,
            color: "#202020",
            flex: 1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {stepLabel}{message}
        </span>

        {/* Close button with spinning loader ring */}
        <button
          onClick={onClose}
          style={{
            position: "relative",
            width: 32,
            height: 32,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            padding: 0,
          }}
        >
          <Loader2
            size={32}
            color="#1570EF"
            strokeWidth={2}
            style={{
              position: "absolute",
              animation: "tx-toast-spin 1.2s linear infinite",
            }}
          />
          <X size={14} color="#1570EF" strokeWidth={2.5} />
        </button>
      </div>

      <style>{`
        @keyframes tx-toast-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

/**
 * Show a blocking transaction toast at the bottom-right of the screen.
 * Nothing else on the page can be interacted with while visible.
 *
 * @param {string} message - The message to display.
 * @param {number} [step] - Current step number (for multi-step flows).
 * @param {number} [totalSteps] - Total number of steps (for multi-step flows).
 * @returns {function} dismiss - Call to remove the toast.
 */
export const showTransactionToast = (message, step = 1, totalSteps = 1) => {
  // Remove any existing toast first
  dismissTransactionToast();

  const container = document.createElement("div");
  container.id = "tx-toast-container";
  document.body.appendChild(container);
  currentToastContainer = container;

  const root = createRoot(container);
  currentToastRoot = root;

  root.render(
    <TransactionToastContent
      message={message}
      step={step}
      totalSteps={totalSteps}
      onClose={() => dismissTransactionToast()}
    />
  );

  return dismissTransactionToast;
};

/**
 * Update the message on the currently visible transaction toast without
 * recreating it (avoids flicker).
 *
 * @param {string} message - New message to display.
 * @param {number} [step] - Current step number.
 * @param {number} [totalSteps] - Total number of steps.
 */
export const updateTransactionToast = (message, step = 1, totalSteps = 1) => {
  if (currentToastRoot && currentToastContainer) {
    currentToastRoot.render(
      <TransactionToastContent
        message={message}
        step={step}
        totalSteps={totalSteps}
        onClose={() => dismissTransactionToast()}
      />
    );
  } else {
    showTransactionToast(message, step, totalSteps);
  }
};

/**
 * Dismiss the currently visible transaction toast (if any).
 */
export const dismissTransactionToast = () => {
  if (currentToastRoot) {
    currentToastRoot.unmount();
    currentToastRoot = null;
  }
  if (currentToastContainer && currentToastContainer.parentNode) {
    currentToastContainer.parentNode.removeChild(currentToastContainer);
    currentToastContainer = null;
  }
};

// ---------------------------------------------------------------------------
// Unified helpers — use these everywhere (EVM & Cosmos) so all tx feedback
// goes through one path.
// ---------------------------------------------------------------------------

/**
 * Show a final success toast (bottom-right react-toastify) and dismiss the
 * blocking pending toast.
 *
 * @param {string|JSX.Element} message - Success message or JSX.
 */
export const showTxSuccess = (message) => {
  dismissTransactionToast();
  toast.dismiss();
  toast.success(message);
};

/**
 * Show a final error toast (bottom-right react-toastify) and dismiss the
 * blocking pending toast.
 *
 * @param {string} message - Error message.
 */
export const showTxError = (message) => {
  dismissTransactionToast();
  toast.dismiss();
  toast.error(message);
};
